import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Search, Download } from "lucide-react";

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

export function ItemsTable({ view, columns, emptyMsg, testid }) {
  const [demo, setDemo] = useState(false);
  const [targetDays, setTargetDays] = useState(45);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("sku") || ""; } catch { return ""; }
  });
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);
  const perPage = 15;

  useEffect(() => {
    setLoading(true);
    api.get("/analytics/items", { params: { demo, target_days: targetDays, view } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [demo, targetDays, view]);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    const ql = q.toLowerCase().trim();
    let out = ql ? items.filter((i) => `${i.sku} ${i.brand} ${i.model} ${i.tire_size}`.toLowerCase().includes(ql)) : items;
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [data, q, sort]);

  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const exportCSV = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${view}-export.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const changeSort = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });

  return (
    <DashboardLayout demoBanner={demo} demoMode={demo} onToggleDemo={() => setDemo(!demo)}>
      <div className="flex items-center flex-wrap gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid={`${testid}-heading`}>{view === "reorder" ? "Reorder" : view === "slow" ? "Slow Inventory" : view === "pricing" ? "Pricing Intelligence" : "All Items"}</h1>
          <p className="mt-1 text-sm text-[#3B424B]">{data?.count ?? 0} SKUs · target coverage {targetDays} days</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex bg-[var(--tm-off)] rounded-lg p-1 border border-[var(--tm-border)]">
            {[30, 45, 60].map((d) => (
              <button key={d} onClick={() => setTargetDays(d)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${targetDays === d ? "bg-white text-[#0F1115]" : "text-[#3B424B]"}`} data-testid={`${testid}-target-${d}`}>{d}d</button>
            ))}
          </div>
          <button onClick={exportCSV} className="tm-btn-outline h-10 px-3" data-testid={`${testid}-export`}><Download size={14} /> Export</button>
        </div>
      </div>

      <div className="mt-5 tm-card bg-white overflow-hidden">
        <div className="p-4 border-b border-[var(--tm-border)] flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3B424B]" />
            <input placeholder="Search SKU, brand, size…" className="tm-input pl-9 h-10" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid={`${testid}-search`} />
          </div>
        </div>
        {loading ? <div className="p-6 text-sm text-[#3B424B]">Loading…</div> : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#3B424B]" data-testid={`${testid}-empty`}>{emptyMsg}</div>
        ) : (
          <div className="overflow-x-auto tm-scroll">
            <table className="w-full text-sm">
              <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]">
                <tr>{columns.map((c) => (
                  <th key={c.key} className="p-3 cursor-pointer select-none" onClick={() => changeSort(c.key)} data-testid={`${testid}-sort-${c.key}`}>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</th>
                ))}</tr>
              </thead>
              <tbody>
                {paged.map((r, idx) => (
                  <tr key={`${r.sku}-${idx}`} className="border-t border-[var(--tm-border)]" data-testid={`${testid}-row-${r.sku}`}>
                    {columns.map((c) => <td key={c.key} className={`p-3 ${c.mono ? "font-mono" : ""} ${c.align === "right" ? "text-right" : ""}`}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > perPage && (
          <div className="p-4 border-t border-[var(--tm-border)] flex items-center justify-between text-sm">
            <div className="text-[#3B424B]">Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <button className="tm-btn-outline h-9 px-3" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Previous</button>
              <button className="tm-btn-outline h-9 px-3" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Next</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const ConfBadge = ({ v }) => {
  const map = { high: "bg-[#14624A]/10 text-[#14624A]", medium: "bg-[#C9943D]/15 text-[#8a6318]", low: "bg-[#3B424B]/10 text-[#3B424B]" };
  return <span className={`tm-badge ${map[v] || map.low}`}>{v}</span>;
};

export default function ReorderPage() {
  const columns = [
    { key: "sku", label: "SKU", mono: true },
    { key: "brand", label: "Brand" },
    { key: "tire_size", label: "Size", mono: true },
    { key: "quantity_on_hand", label: "QOH", mono: true, align: "right" },
    { key: "avg_daily_sales", label: "Avg/day", mono: true, align: "right", render: (r) => r.avg_daily_sales?.toFixed?.(2) ?? "—" },
    { key: "days_of_supply", label: "Days Supply", mono: true, align: "right", render: (r) => r.days_of_supply ?? "—" },
    { key: "reorder_quantity", label: "Reorder Qty", mono: true, align: "right" },
    { key: "confidence", label: "Confidence", render: (r) => <ConfBadge v={r.confidence} /> },
  ];
  return <ItemsTable view="reorder" columns={columns} emptyMsg="No reorder recommendations right now." testid="reorder" />;
}
